import {AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {delay, distinctUntilChanged, skip, take, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {merge, of, Subscription} from 'rxjs';
import {SelectionModel} from '@angular/cdk/collections';
import {select, Store} from '@ngrx/store';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject} from 'rxjs';
import {Location} from '@angular/common';
import {
    AssignmentModel, AuditTrailActionToggleLoading, AuditTrailDeleted, AuditTrailModel, AuditTrailOnServerAdminRestored,
    AuditTrailOnServerRestored,
    AuditTrailsClear,
    AuditTrailsDataSource,
    AuditTrailsPageRequested, selectAdminTrashedAuditTrailsCount, selectAssignmentById,
    selectAuditTrailsLastQuery, selectTrashedAuditTrailsCount
} from '../../../../../core/assignment';
import {LayoutUtilsService, QueryParamsModel} from '../../../../../core/_base/crud';
import {SubheaderService} from '../../../../../core/_base/layout';
import {AuditTrailsService} from '../../../../../core/assignment/_services';
import {AppState} from '../../../../../core/reducers';
import {AuditReportDialogComponent} from '../../../shared_components/audit-report-dialog/audit-report-dialog.component';
import {User, currentUser} from 'src/app/core/mad-auth/mad-auth.store';
import { selectToesInStore, ToeModel, ToesPageRequested } from 'src/app/core/toe';

@Component({
    selector: 'kt-audit-list',
    templateUrl: './audit-list.component.html',
    styleUrls: ['./audit-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditListComponent implements OnInit, OnDestroy, AfterViewInit {

    // Table fields
    dataSource: AuditTrailsDataSource;
    trashCnt = 0;
    adminTrashCnt = 0;
    backTitle: string;
    adminTrashedCnt: number;
    displayedColumns = ['id',
        'interaction_date',
        'client_name',
        'terms_of_engagement_name',
        'asset_class_name',
        'impact_on_valuation',
        'actions'];

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild('sort1', {static: true}) sort: MatSort;
    // Selection
    selection = new SelectionModel<AuditTrailModel>(true, []);
    auditTrailResult: AuditTrailModel[] = [];
    assignmentId: number;
    assignment: AssignmentModel;
    filterToE = '';
    // toes: any[] = [];
    toes: ToeModel[] = [];
    currentUser: User;

    lastQueryParams: QueryParamsModel;

    menuSubject = new BehaviorSubject<number>(-1);
    menuItems = [];

    // Subscriptions
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute
     * @param router
     * @param dialog: MatDialog
     * @param location: Location
     * @param subheaderService
     * @param layoutUtilsService: LayoutUtilsService
     * @param service
     * @param translate: TranslateService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                public dialog: MatDialog,
                public location: Location,
                public subheaderService: SubheaderService,
                private layoutUtilsService: LayoutUtilsService,
                public service: AuditTrailsService,
                private translate: TranslateService,
                private store: Store<AppState>) {
        this.menuItems = [
            {
                title: this.translate.instant('AUDIT_TRAIL.LIST.MENU.NEW'),
                icon: 'flaticon2-edit',
                refId: 1,
                disabled: false
            },
            // {
            //     title: this.translate.instant('AUDIT_TRAIL.LIST.MENU.REPORT'),
            //     icon: 'flaticon2-writing',
            //     refId: 2,
            //     disabled: false
            // }
        ];
        this.trashCnt = 0;
        this.adminTrashedCnt = 0;
        this.backTitle = this.translate.instant('GENERAL.TOOLTIP.BACK', {
            back_page: this.translate.instant('ASSIGNMENT.LIST.TITLE')
        });
    }

    ngOnInit() {
        this.lastQueryParams = new QueryParamsModel({});

        this.subheaderService.setTitle(this.translate.instant('SUBHEADER.INTERACTIONS'));
        this.subheaderService.setBreadcrumbs([
            {title: 'Main menu', page: `../default/assignment`},
            {title: 'Assignments', page: `../default/assignment/assignments`},
            {title: 'Interactions'},
        ]);

        if (localStorage.getItem('audit_trails_url') && localStorage.getItem('audit_trails_url') === 'dashboard') {
            this.backTitle = this.translate.instant('GENERAL.TOOLTIP.BACK', {
                back_page: this.translate.instant('AUDIT_TRAIL.LIST.TOE_DASHBOARD')
            });
            localStorage.removeItem('audit_trails_url');
        }

        const menuSubjectSubscribe = this.menuSubject.asObservable().subscribe(refId => {
            switch (refId) {
                case 1:
                    this.router.navigate(['add'], {relativeTo: this.activatedRoute});
                    break;
                case 2:
                    const dialogRef = this.dialog.open(AuditReportDialogComponent, {data: {assId: this.assignmentId}});

                    dialogRef.afterClosed().subscribe(res => {
                        if (!res) {
                            return;
                        }

                    });
                    break;
                default:
                    break;
            }
        });
        this.subscriptions.push(menuSubjectSubscribe);


        this.store.dispatch(new AuditTrailsClear());
        this.assignmentId = Number(this.activatedRoute.snapshot.paramMap.get('id'));
        this.paginator._intl.itemsPerPageLabel = this.translate.instant('GENERAL.PAGELABEL');
        // If the user changes the sort order, reset back to the first page.
        const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.subscriptions.push(sortSubscription);

        this.store.pipe(select(selectAssignmentById(this.assignmentId))).subscribe(res => {
            if (res) {
                this.assignment = res;
            }
        });

        /* Data load will be triggered in two cases:
        - when a pagination event occurs => this.paginator.page
        - when a sort event occurs => this.sort.sortChange
        **/
        const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page).pipe(
            tap(() => this.loadAuditTrailsList())
        )
            .subscribe();
        this.subscriptions.push(paginatorSubscriptions);


        const userSubscription = this.store.pipe(
            take(1),
            select(currentUser))
            .subscribe(res => {
                if (res) {
                    this.currentUser = res;
                }
            });
        this.subscriptions.push(userSubscription);

        // Init DataSource
        this.dataSource = new AuditTrailsDataSource(this.store);
        const entitiesSubscription = this.dataSource.entitySubject.pipe(
            skip(1),
            distinctUntilChanged()
        ).subscribe(res => {
            this.auditTrailResult = res;
        });
        this.subscriptions.push(entitiesSubscription);

        const selectTrashedSubscription = this.store.pipe(
            select(selectTrashedAuditTrailsCount)
        ).subscribe(res => {
            this.trashCnt = res;
        });
        this.subscriptions.push(selectTrashedSubscription);

        const selectAdminTrashedSubscription = this.store.pipe(
            select(selectAdminTrashedAuditTrailsCount)
        ).subscribe(res => {
            this.adminTrashCnt = res;
        });
        this.subscriptions.push(selectAdminTrashedSubscription);

        // First load
        of(undefined).pipe(take(1), delay(1000)).subscribe(() => { // Remove this line, just loading imitation
            this.loadAuditTrailsList();
        }); // Remove this line, just loading imitation

        const queryParams = new QueryParamsModel(
            {name: ''},
            'asc',
            'status',
            1,
            1000
        );

        this.store.dispatch(new ToesPageRequested({page: queryParams, assignmentId: this.assignmentId}));
        
        const toesSubscription = this.store.pipe(
            select(selectToesInStore))
            .subscribe(res => {
                if (res) {
                    this.toes = res.items;
                }
            });
        
        this.subscriptions.push(toesSubscription);

        const lastQuerySubscriptions = this.store.pipe(
            select(selectAuditTrailsLastQuery),
            take(1)
        ).subscribe(res => {
            if (res.isEmpty()) {
                return;
            }
            this.lastQueryParams = res;
            this.updateFilter(res.filter);
        });
        this.subscriptions.push(lastQuerySubscriptions);
    }


    ngAfterViewInit(): void {
        if (this.activatedRoute.snapshot.queryParamMap.has('show-trash')) {
            this.trash();
        }

        if (this.activatedRoute.snapshot.queryParamMap.has('show-admin-trash')) {
            this.adminTrash();
        }
    }

    /**
     * On Destroy
     */
    ngOnDestroy() {
        this.subscriptions.forEach(el => el.unsubscribe());
    }

    /**
     * Load Assignments List from service through data-source
     */
    loadAuditTrailsList() {
        this.selection.clear();
        let queryParams = null;
        if (this.lastQueryParams.isEmpty()) {
            queryParams = new QueryParamsModel(
                this.filterConfiguration(),
                this.sort.direction,
                this.sort.active,
                this.paginator.pageIndex + 1,
                this.paginator.pageSize
            );
        } else {
            queryParams = this.lastQueryParams;
        }
        // Call request from server
        this.store.dispatch(new AuditTrailsPageRequested({page: queryParams, assignmentId: this.assignmentId}));
        this.selection.clear();
    }

    /**
     * Returns object for filter
     */
    filterConfiguration(): any {
        const filter: any = {};
        const searchText = '';

        if (this.filterToE && this.filterToE.length > 0) {
            filter.status = +this.filterToE;
        }

        filter.name = searchText;
        if (!searchText) {
            return filter;
        }
        return filter;
    }

    valueChange() {
        this.lastQueryParams = new QueryParamsModel({});
        this.loadAuditTrailsList();
    }

    updateFilter(filter: any) {
        this.filterToE = filter.status.toString();
    }

    /** ACTIONS */
    /**
     * Delete interaction
     *
     * @param _item: AuditTrailModel
     */
    deleteInteraction(_item: AuditTrailModel) {
        const _title = this.translate.instant('AUDIT_TRAIL.LIST.DIALOG.DELETE.TITLE');
        // const _description = 'The Audit Trail on interaction date \"' + _item.interaction_date + '\" will be deleted';
        const _description = this.translate.instant('AUDIT_TRAIL.LIST.DIALOG.DELETE.DESCRIPTION', {
            date: _item.interaction_date
        });
        const _waitDesciption = this.translate.instant('AUDIT_TRAIL.LIST.DIALOG.DELETE.WAIT_DESCRIPTION');

        const dialogRef = this.layoutUtilsService.deleteElementWithDescr(_title, _description, _waitDesciption);
        dialogRef.afterClosed().subscribe(res => {
            if (!res) {
                return;
            }
            this.store.dispatch(new AuditTrailDeleted({id: _item.id, trashDeleteDesc: res, userId: this.currentUser.id}));
        });
    }

    reachInteraction(auditTrail: AuditTrailModel) {
        this.router.navigate(['../audit-trails/show/', auditTrail.id], {relativeTo: this.activatedRoute});
    }

    /**
     * Check all rows are selected
     */
    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.auditTrailResult.length;
        return numSelected === numRows;
    }

    /**
     * Toggle selection
     */
    masterToggle() {
        if (this.selection.selected.length === this.auditTrailResult.length) {
            this.selection.clear();
        } else {
            this.auditTrailResult.forEach(row => this.selection.select(row));
        }
    }

    back() {
        if (this.backTitle === this.translate.instant('GENERAL.TOOLTIP.BACK', {
                back_page: this.translate.instant('ASSIGNMENT.LIST.TITLE')
            })) {
            this.router.navigate(['../toes'], {relativeTo: this.activatedRoute});
            return;
        }
        this.location.back();
    }


    // Fetch trash data
    trash() {
        this.store.dispatch(new AuditTrailActionToggleLoading({isLoading: true}));
        this.service.getTrashed(this.assignmentId).subscribe(res => {

            this.store.dispatch(new AuditTrailActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({

                    text: elem.interaction_description,
                    interaction_date: elem.interaction_date,
                    desc: `${elem.trash_delete_descr}`,
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    date: elem.user_deleted,
                    userName: elem.deleted_user_name
                });
            });
            this.show_trash(items);
        });
    }

    // Fetch admin trash data
    adminTrash() {
        this.store.dispatch(new AuditTrailActionToggleLoading({isLoading: true}));
        this.service.getAdminTrashed(this.assignmentId).subscribe(res => {

            this.store.dispatch(new AuditTrailActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: elem.interaction_description,
                    interaction_date: elem.interaction_date,
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    date: elem.user_deleted,
                    userName: elem.deleted_user_name
                });
            });
            this.show_trash(items, true);
        });
    }

    /**
     * Show Edit agency dialog and save after success close result
     * @param items: any[]
     * @param isAdmin: boolean
     */
    show_trash(items: any[], isAdmin = false) {
        const _title = this.translate.instant('AUDIT_TRAIL.LIST.DIALOG.SHOW_TRASH.TITLE');
        const dialogRef = this.layoutUtilsService.trashedAuditTrailEntities(_title, items, this);
        dialogRef.afterClosed().subscribe(res => {
            if (!res) {
                return;
            }
            this.router.navigate(['../audit-trails/trash-detail/', res.id], {relativeTo: this.activatedRoute});
        });
    }

    restore(id: number, isAdmin = false) {
        if (isAdmin) {
            this.store.dispatch(new AuditTrailOnServerAdminRestored({id}));
        } else {
            this.store.dispatch(new AuditTrailOnServerRestored({id}));
        }
    }
}
